/* eslint-disable max-lines */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import JsonView from '@uiw/react-json-view';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getShipmentEventHistoryApi } from 'api/order';
import { getShipmentAuditEventsApi } from 'api/shipment';
import { casingFormat } from 'common/utils';
import useWebSocket from 'common/hooks/useWebSocket';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatCarrierService = (code) => {
  if (!code) return '';
  return code
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const shipmentSourceLabel = {
  PRX: 'PRX',
  PRX_SHIPMENT: 'PRX',
  SHIPSTATION: 'ShipStation',
  EXTERNAL: 'External',
};

const sourceTagEventTypes = new Set([
  'ORDER_SHIPPED',
  'SHIPMENT_CANCELLED',
  'SHIPMENT_VOIDED',
  'ORDER_VOIDED',
]);

const shipmentModeLabel = {
  SHIP_VIA_SHIPSTATION: 'via ShipStation',
  EXTERNAL_SHIPMENT: 'Externally Shipped',
  MANUAL: 'Manual Shipment',
  PRX_SHIPMENT: 'via PRX',
};

const formatShipmentMode = (mode) => shipmentModeLabel[mode] || mode?.replace(/_/g, ' ') || '';

const formatAddress = (addr) => {
  if (!addr || typeof addr !== 'object') return '—';
  return [addr.line1, addr.line2, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
};

const omittedEventTypes = ['ORDER_SENT_TO_SHIPPING'];

export const failedEventTypes = [
  'SHIPMENT_CREATE_FAILED',
  'SHIPMENT_UPDATE_FAILED',
  'SHIPMENT_CANCEL_FAILED',
  'SHIPMENT_VOID_FAILED',
  'SHIPMENT_LABEL_CREATION_FAILED',
  'RATE_REQUEST_FAILED',
  'SHIPMENT_TRANSFER_FAILED',
  'SHIPMENT_ADDRESS_UPDATE_FAILED',
  'LABEL_RETRIEVAL_FAILED',
  'PRINT_JOB_FAILED',
  'PRINTER_NOT_FOUND',
];

export const eventTitleMap = {
  ORDER_CREATED_IN_SHIPSTATION: 'Order Created in ShipStation',
  ORDER_SHIPPED: 'Order Shipped',
  ORDER_SENT_TO_SHIPPING: 'Order sent for shipment creation',
  BACK_TO_ORDER_CREATED_IN_SHIPSTATION: 'Order Returned to ShipStation',
  SHIPMENT_CREATED: 'Shipment Created',
  MANUAL_SHIPMENT_CREATED: 'Manual Shipment Created',
  SHIPMENT_LABEL_CREATED: 'Shipment Label Created',
  LABEL_GENERATION_REQUESTED: 'Label Generation Requested',
  SHIPMENT_CONFIG_CHANGED: 'Shipment Config Changed',
  SHIPMENT_UPDATE_FAILED: 'Shipment Update Failed',
  SHIPMENT_ADDRESS_UPDATED: 'Address Updated',
  SHIPMENT_ADDRESS_UPDATE_FAILED: 'Address Update Failed',
  ORDER_VOIDED: 'Order Voided',
  SHIPMENT_VOIDED: 'Shipment Voided',
  SHIPMENT_VOID_FAILED: 'Void Failed',
  SHIPMENT_CANCELLED: 'Shipment Cancelled',
  SHIPMENT_CANCEL_FAILED: 'Cancellation Failed',
  SHIPMENT_LABEL_CREATION_FAILED: 'Label Creation Failed',
  SHIPMENT_CREATE_FAILED: 'Shipment Create Failed',
  SHIPMENT_TRANSFERRED_TO_SHIPSTATION: 'Transferred to ShipStation',
  SHIPMENT_TRANSFER_FAILED: 'Transfer Failed',
  RATE_REQUEST_INITIATED: 'Rate Request Initiated',
  RATE_RECEIVED: 'Rate Received',
  RATE_REQUEST_FAILED: 'Rate Request Failed',
  SHIPMENT_DIALOG_OPENED: 'Shipment Dialog Opened',
  LABEL_RETRIEVAL_STARTED: 'Label Retrieval Started',
  LABEL_RETRIEVAL_COMPLETED: 'Label Retrieval Completed',
  LABEL_RETRIEVAL_FAILED: 'Label Retrieval Failed',
  PRINT_JOB_SUBMITTED: 'Print Job Submitted',
  PRINT_JOB_COMPLETED: 'Print Job Completed',
  PRINT_JOB_FAILED: 'Print Job Failed',
  PRINTER_IDENTIFIED: 'Printer Identified',
  PRINTER_DISCOVERY_STARTED: 'Printer Discovery Started',
  PRINTER_NOT_FOUND: 'Printer Not Found',
  PRINT_REQUESTED: 'Print Requested',
  SATURDAY_DELIVERY_APPLIED: 'Saturday Delivery Applied',
  FEDEX_SHIPMENT_PICKED_UP: 'Shipment Picked Up',
  SHIPMENT_LABEL_DOWNLOADED: 'Shipment Label Downloaded',
  SHIPMENT_ITEM_REMOVED: 'Shipment Item Removed',
  SHIPMENT_ITEM_ADDED: 'Shipment Item Added',
  FEDEX_SHIPMENT_DELIVERED: 'Shipment Delivered',
  FEDEX_SHIPMENT_OUT_FOR_DELIVERY: 'Shipment Out for Delivery',
  CONFIRM_SHIPMENT_PACKED: 'Shipment Packed',
};

const statusLabelMap = {
  ORDER_CREATED_IN_SHIPSTATION: 'Created in ShipStation',
  ORDER_SHIPPED: 'Order Shipped',
  ORDER_SENT_TO_SHIPPING: 'Sent to Shipping',
  BACK_TO_ORDER_CREATED_IN_SHIPSTATION: 'Returned to ShipStation',
  SHIPMENT_CREATED: 'Created',
  MANUAL_SHIPMENT_CREATED: 'Created',
  SHIPMENT_LABEL_CREATED: 'Label Created',
  LABEL_GENERATION_REQUESTED: 'Generating Label',
  SHIPMENT_CONFIG_CHANGED: 'Config Changed',
  SHIPMENT_ADDRESS_UPDATED: 'Address Updated',
  ORDER_VOIDED: 'Voided',
  SHIPMENT_VOIDED: 'Label Voided',
  SHIPMENT_CANCELLED: 'Cancelled',
  SHIPMENT_TRANSFERRED_TO_SHIPSTATION: 'Transferred',
  RATE_RECEIVED: 'Rate Received',
  RATE_REQUEST_INITIATED: 'Rate Requested',
  SHIPMENT_CREATE_FAILED: 'Create Failed',
  SHIPMENT_LABEL_CREATION_FAILED: 'Label Failed',
  RATE_REQUEST_FAILED: 'Rate Failed',
  SHIPMENT_CANCEL_FAILED: 'Cancel Failed',
  SHIPMENT_VOID_FAILED: 'Void Failed',
  SHIPMENT_UPDATE_FAILED: 'Update Failed',
  SHIPMENT_TRANSFER_FAILED: 'Transfer Failed',
  SHIPMENT_ADDRESS_UPDATE_FAILED: 'Address Update Failed',
  SATURDAY_DELIVERY_APPLIED: 'Saturday Delivery',
  FEDEX_SHIPMENT_PICKED_UP: 'Picked Up',
  SHIPMENT_LABEL_DOWNLOADED: 'Label Downloaded',
  SHIPMENT_ITEM_REMOVED: 'Item Removed',
  SHIPMENT_ITEM_ADDED: 'Item Added',
  CONFIRM_SHIPMENT_PACKED: 'Packed',
};

const eventIconMap = {
  ORDER_CREATED_IN_SHIPSTATION: { Icon: LocalShippingOutlinedIcon, color: 'info' },
  ORDER_SHIPPED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  ORDER_SENT_TO_SHIPPING: { Icon: LocalShippingOutlinedIcon, color: 'success' },
  BACK_TO_ORDER_CREATED_IN_SHIPSTATION: { Icon: SyncIcon, color: 'warning' },
  SHIPMENT_CREATED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  MANUAL_SHIPMENT_CREATED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  SHIPMENT_LABEL_CREATED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  LABEL_GENERATION_REQUESTED: { Icon: LabelOutlinedIcon, color: 'info' },
  SHIPMENT_CONFIG_CHANGED: { Icon: TuneIcon, color: 'warning' },
  SHIPMENT_ADDRESS_UPDATED: { Icon: LocationOnOutlinedIcon, color: 'warning' },
  ORDER_VOIDED: { Icon: CancelOutlinedIcon, color: 'error' },
  SHIPMENT_VOIDED: { Icon: CancelOutlinedIcon, color: 'error' },
  SHIPMENT_CANCELLED: { Icon: CancelOutlinedIcon, color: 'error' },
  SHIPMENT_TRANSFERRED_TO_SHIPSTATION: { Icon: LocalShippingOutlinedIcon, color: 'success' },
  RATE_REQUEST_INITIATED: { Icon: SyncIcon, color: 'info' },
  RATE_RECEIVED: { Icon: AttachMoneyIcon, color: 'success' },
  SHIPMENT_DIALOG_OPENED: { Icon: HourglassEmptyIcon, color: 'info' },
  SATURDAY_DELIVERY_APPLIED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  FEDEX_SHIPMENT_PICKED_UP: { Icon: LocalShippingOutlinedIcon, color: 'success' },
  SHIPMENT_LABEL_DOWNLOADED: { Icon: LabelOutlinedIcon, color: 'success' },
  SHIPMENT_ITEM_REMOVED: { Icon: SyncIcon, color: 'warning' },
  SHIPMENT_ITEM_ADDED: { Icon: SyncIcon, color: 'success' },
  FEDEX_SHIPMENT_DELIVERED: { Icon: CheckCircleOutlineIcon, color: 'success' },
  FEDEX_SHIPMENT_OUT_FOR_DELIVERY: { Icon: CheckCircleOutlineIcon, color: 'success' },
  CONFIRM_SHIPMENT_PACKED: { Icon: Inventory2OutlinedIcon, color: 'success' },
};

export const getEventDotColor = (event) => {
  if (failedEventTypes.includes(event.auditEventType)) return 'error';
  return (eventIconMap[event.auditEventType] || { color: 'info' }).color;
};

const getTitleColor = (status) => {
  if (status === 'FAILED') return 'error.main';
  if (status === 'STARTED') return 'info.main';
  return 'text.primary';
};

// Returns string or string[] for compact subtitle
export const getEventKeyInfo = (event) => {
  const data = event.auditEventData || {};
  const carrier = data.carrier || '';
  const service = formatCarrierService(data.carrierServiceCode);
  const tracking = data.trackingNumber || '';
  const performedByName = data.performedByName ? casingFormat(data.performedByName) : '';
  const errorMessage = data.errorMessage || '';
  const cost = data.cost != null ? `$${Number(data.cost).toFixed(2)}` : '';
  const referenceId = data.referenceId || '';
  const { auditEventType: type } = event;

  if (type === 'ORDER_CREATED_IN_SHIPSTATION') {
    const parts = [];
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    if (service) parts.push(`Service: ${service}`);
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (data.spOrderId) parts.push(`SS Order: ${data.spOrderId}`);
    return parts.join(' • ') || 'Order created in ShipStation';
  }

  if (type === 'ORDER_SHIPPED') {
    const parts = [];
    if (data.shipmentMode) parts.push(formatShipmentMode(data.shipmentMode));
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    if (service) parts.push(`Service: ${service}`);
    if (cost) parts.push(`Cost: ${cost}`);
    return parts.join(' • ') || 'Order shipped';
  }

  if (type === 'ORDER_SENT_TO_SHIPPING') {
    const parts = [];
    if (data.shipmentMode) parts.push(formatShipmentMode(data.shipmentMode));
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    if (service) parts.push(`Service: ${service}`);
    return parts.join(' • ') || 'Manual shipment created';
  }

  if (type === 'CONFIRM_SHIPMENT_PACKED') return '';

  if (type === 'BACK_TO_ORDER_CREATED_IN_SHIPSTATION') {
    return 'Shipment voided — order returned to ShipStation';
  }

  if (type === 'SHIPMENT_LABEL_CREATED') {
    const parts = [];
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (cost) parts.push(`Cost: ${cost}`);
    return parts.join(' • ') || 'Label generated';
  }
  if (type === 'LABEL_GENERATION_REQUESTED') {
    return performedByName ? `Requested by ${performedByName}` : 'Label generation requested';
  }
  if (type === 'SHIPMENT_CONFIG_CHANGED') {
    if (!Array.isArray(data.changedFields) || data.changedFields.length === 0) {
      return 'Shipment configuration updated';
    }
    const formatDimensions = (val) => {
      try {
        const d = JSON.parse(val);
        if (!d) return '0 × 0 × 0 in';
        return `${d.length ?? 0} × ${d.width ?? 0} × ${d.height ?? 0} ${d.unit || 'in'}`;
      } catch (e) {
        return val || '0 × 0 × 0 in';
      }
    };
    const formatWeight = (val) => {
      try {
        const w = JSON.parse(val);
        return `${w.value} ${w.unit || 'lb'}`;
      } catch (e) {
        return val;
      }
    };
    const lines = data.changedFields
      .filter((f) => f && f.field)
      .map((f) => {
        const prev = f.previous ?? '';
        const upd = f.updated ?? '';
        if (f.field === 'service') {
          return `Service: ${formatCarrierService(prev)} → ${formatCarrierService(upd)}`;
        }
        if (f.field === 'carrier') {
          return `Carrier: ${formatCarrierService(prev)} → ${formatCarrierService(upd)}`;
        }
        if (f.field === 'packageCode') {
          return `Package: ${formatCarrierService(prev)} → ${formatCarrierService(upd)}`;
        }
        if (f.field === 'shipDate') {
          return `Ship Date: ${prev || 'None'} → ${upd || 'None'}`;
        }
        if (f.field === 'dimensions') {
          return `Dimensions: ${formatDimensions(prev)} → ${formatDimensions(upd)}`;
        }
        if (f.field === 'weight') {
          return `Weight: ${formatWeight(prev)} → ${formatWeight(upd)}`;
        }
        if (f.field === 'enableSaturdayDelivery') {
          return `Saturday Delivery: ${prev ? 'Yes' : 'No'} → ${upd ? 'Yes' : 'No'}`;
        }
        if (f.field === 'insuranceAmount') {
          return `Insurance: $${prev} → $${upd}`;
        }
        if (f.field === 'signatureRequired') {
          return `Signature Required: ${prev ? 'Yes' : 'No'} → ${upd ? 'Yes' : 'No'}`;
        }
        const label = String(f.field)
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (c) => c.toUpperCase());
        return `${label}: ${prev} → ${upd}`;
      });
    if (cost) lines.push(`Rate: ${cost}`);
    return lines;
  }
  if (type === 'SHIPMENT_VOIDED') {
    const parts = [];
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    if (service) parts.push(`Service: ${service}`);
    return parts.join(' • ') || 'Label voided';
  }
  if (type === 'SHIPMENT_CANCELLED') {
    return tracking ? `Shipment cancelled (Tracking: ${tracking})` : 'Shipment cancelled';
  }
  if (type === 'ORDER_VOIDED') {
    const parts = [];
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    return parts.join(' • ') || 'Order voided';
  }
  if (type === 'SHIPMENT_ADDRESS_UPDATED') return '';
  if (type === 'RATE_RECEIVED') return cost || 'Rate received';
  if (type === 'RATE_REQUEST_INITIATED') return '';
  if (type === 'SHIPMENT_DIALOG_OPENED') {
    return performedByName ? `Opened by ${performedByName}` : '';
  }
  if (type === 'SHIPMENT_LABEL_DOWNLOADED') {
    return performedByName ? `Downloaded by ${performedByName}` : '';
  }
  if (type === 'SATURDAY_DELIVERY_APPLIED') {
    const parts = [];
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    if (data.carrierServiceCode)
      parts.push(`Service: ${formatCarrierService(data.carrierServiceCode)}`);
    if (data.shipDate) parts.push(`Ship Date: ${data.shipDate}`);
    return parts.join(' • ') || 'Saturday delivery applied';
  }
  if (type === 'FEDEX_SHIPMENT_PICKED_UP') {
    const parts = [];
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    return parts.join(' • ') || 'Picked up by FedEx';
  }
  if (type === 'SHIPMENT_ITEM_REMOVED') {
    const removedIds = data.removedItemReferenceIds || [];
    const removedItems = data.removedItems || [];
    let count = 0;
    if (Array.isArray(removedIds)) count = removedIds.length;
    else if (Array.isArray(removedItems)) count = removedItems.length;
    const toRef = data.movedToReferenceId || data.movedToShipmentRequestId || '';
    if (count && toRef) return `Item(${count}) removed to ${toRef}`;
    if (count) return `Item(${count}) removed`;
    return '';
  }
  if (type === 'SHIPMENT_ITEM_ADDED') {
    const addedIds = data.addedItemReferenceIds || [];
    const addedItems = data.addedItems || [];
    let count = 0;
    if (Array.isArray(addedIds)) count = addedIds.length;
    else if (Array.isArray(addedItems)) count = addedItems.length;
    const fromRef = data.movedFromReferenceId || data.movedFromShipmentRequestId || '';
    if (count && fromRef) return `Item(${count}) added from ${fromRef}`;
    if (count) return `Item(${count}) added`;
    return '';
  }
  if (type === 'RATE_REQUEST_FAILED') return errorMessage || 'Rate request failed';
  if (type === 'SHIPMENT_CREATED') {
    return service || carrier || `Order: ${referenceId}`;
  }
  if (type === 'MANUAL_SHIPMENT_CREATED') {
    return referenceId ? `Order: ${referenceId}` : 'Manual shipment created';
  }
  if (type === 'SHIPMENT_TRANSFERRED_TO_SHIPSTATION') return 'Transferred to ShipStation';
  if (
    type === 'PRINTER_IDENTIFIED' ||
    type === 'PRINT_JOB_SUBMITTED' ||
    type === 'PRINT_JOB_COMPLETED'
  ) {
    const printerName = data.metadata?.printerName || '';
    return printerName ? `Printer: ${printerName}` : '';
  }
  if (type === 'PRINTER_NOT_FOUND') return data.metadata?.errorMessage || 'Printer not found';
  if (type === 'PRINT_JOB_FAILED') return data.metadata?.errorMessage || 'Print job failed';

  if (type === 'FEDEX_SHIPMENT_DELIVERED' || type === 'FEDEX_SHIPMENT_OUT_FOR_DELIVERY') {
    const parts = [];
    if (tracking) parts.push(`Tracking: ${tracking}`);
    if (carrier) parts.push(`Carrier: ${formatCarrierService(carrier)}`);
    return parts.join(' • ') || 'Picked up by FedEx';
  }

  return errorMessage || '';
};

export const getEventDiff = (event) => {
  const data = event.auditEventData || {};
  if (
    event.auditEventType === 'SHIPMENT_ADDRESS_UPDATED' &&
    data.previousAddress &&
    data.newAddress
  ) {
    return { before: formatAddress(data.previousAddress), after: formatAddress(data.newAddress) };
  }
  return null;
};

export const getRateRequestDetail = (event) => {
  if (event.auditEventType !== 'RATE_REQUEST_INITIATED') return null;
  const data = event.auditEventData || {};
  const rows = [];
  if (data.carrier) rows.push({ label: 'Carrier', value: formatCarrierService(data.carrier) });
  if (data.carrierServiceCode)
    rows.push({ label: 'Service', value: formatCarrierService(data.carrierServiceCode) });
  if (data.carrierPackageCode)
    rows.push({ label: 'Package', value: formatCarrierService(data.carrierPackageCode) });
  rows.push({ label: 'Saturday', value: data.enableSaturdayDelivery ? 'Yes' : 'No' });
  if (data.shipDate) rows.push({ label: 'Ship Date', value: data.shipDate });
  const rawAddr = data.shippingAddress?.address || data.shippingAddress || null;
  const address = rawAddr ? formatAddress(rawAddr) : null;
  return rows.length ? { rows, address } : null;
};

const getExpandLabel = (event) => {
  if (event.auditEventType === 'SHIPMENT_ADDRESS_UPDATED') return 'View address >';
  if (event.auditEventType === 'RATE_REQUEST_INITIATED') return 'View details >';
  if (
    event.auditEventType === 'SHIPMENT_ITEM_ADDED' ||
    event.auditEventType === 'SHIPMENT_ITEM_REMOVED'
  ) {
    const data = event.auditEventData || {};
    const items =
      event.auditEventType === 'SHIPMENT_ITEM_ADDED' ? data.addedItems : data.removedItems;
    if (Array.isArray(items) && items.length > 0) return 'View items >';
  }
  return null;
};

// Events hidden from timeline — verbose snapshots not useful to end users.
const hiddenEventTypes = ['SHIPMENT_UPDATED'];

// Print/label retrieval events are grouped by correlationId into a single collapsible row.
const printGroupTypes = new Set([
  'PRINT_REQUESTED',
  'PRINTER_DISCOVERY_STARTED',
  'PRINTER_IDENTIFIED',
  'PRINT_JOB_SUBMITTED',
  'PRINT_JOB_COMPLETED',
  'PRINT_JOB_FAILED',
  'PRINTER_NOT_FOUND',
]);

const labelRetrievalTypes = new Set([
  'LABEL_RETRIEVAL_STARTED',
  'LABEL_RETRIEVAL_COMPLETED',
  'LABEL_RETRIEVAL_FAILED',
]);

const groupableEventTypes = new Set([...printGroupTypes, ...labelRetrievalTypes]);

const buildRenderItems = (events) => {
  const groups = {};
  const items = [];

  events.forEach((event) => {
    const { auditEventType } = event;
    const correlationId = event.auditEventData?.correlationId;

    if (groupableEventTypes.has(auditEventType) && correlationId) {
      if (!groups[correlationId]) {
        groups[correlationId] = {
          type: 'group',
          correlationId,
          label: printGroupTypes.has(auditEventType) ? 'Print Job' : 'Label Retrieval',
          events: [],
          hasFailed: false,
        };
        items.push(groups[correlationId]);
      }
      groups[correlationId].events.push(event);
      if (failedEventTypes.includes(auditEventType)) {
        groups[correlationId].hasFailed = true;
      }
    } else if (!hiddenEventTypes.includes(auditEventType)) {
      items.push({ type: 'event', event });
    }
  });

  return items;
};

// ─── component ──────────────────────────────────────────────────────────────

const ShippingTimelineModal = ({ open, onClose, shipmentId, referenceId, orderId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [expandedIds, setExpandedIds] = useState([]);
  const [jsonDialogData, setJsonDialogData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resolvedShipmentId, setResolvedShipmentId] = useState(shipmentId);
  const [newEventIds, setNewEventIds] = useState(new Set());
  const [fadingEventIds, setFadingEventIds] = useState(new Set());
  const newEventTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const prevEventIdsRef = useRef(new Set());
  const { on, off, sendMessage } = useWebSocket();

  const fetchAuditEvents = useCallback(
    ({ silent = false } = {}) => {
      const canFetch = orderId ? true : !!(shipmentId && referenceId);
      if (!canFetch) return;
      if (!silent) setLoading(true);
      setError(null);
      const apiCall = orderId
        ? getShipmentEventHistoryApi({ orderId })
        : getShipmentAuditEventsApi({ shipmentId, referenceId });
      apiCall
        .then((data) => {
          if (silent) {
            const allFetched = [...(data?.events || []), ...(data?.errors || [])];
            const addedIds = allFetched
              .map((e) => e.auditEventId)
              .filter((id) => id && !prevEventIdsRef.current.has(id));

            if (addedIds.length > 0) {
              clearTimeout(newEventTimerRef.current);
              clearTimeout(fadeTimerRef.current);
              setNewEventIds(new Set(addedIds));
              setFadingEventIds(new Set());
              fadeTimerRef.current = setTimeout(() => {
                setFadingEventIds(new Set(addedIds));
              }, 9200);
              newEventTimerRef.current = setTimeout(() => {
                setNewEventIds(new Set());
                setFadingEventIds(new Set());
              }, 10000);
            }

            const allIds = allFetched.map((e) => e.auditEventId).filter(Boolean);
            prevEventIdsRef.current = new Set(allIds);
          } else {
            const allFetched = [...(data?.events || []), ...(data?.errors || [])];
            const allIds = allFetched.map((e) => e.auditEventId).filter(Boolean);
            prevEventIdsRef.current = new Set(allIds);
          }

          setAuditData(data);
          if (data?.shipmentRequestId && !resolvedShipmentId) {
            setResolvedShipmentId(data.shipmentRequestId);
          }
          if (!silent) setLoading(false);
        })
        .catch((err) => {
          const gqlError = err?.response?.errors?.[0]?.message;
          setError(gqlError || 'Failed to load shipping timeline. Please try again.');
          if (!silent) setLoading(false);
        });
    },
    [orderId, shipmentId, referenceId, resolvedShipmentId]
  );

  useEffect(() => {
    if (open) {
      fetchAuditEvents();
    } else {
      setResolvedShipmentId(shipmentId);
    }
  }, [open, fetchAuditEvents, shipmentId]);

  useEffect(() => {
    if (!open || !resolvedShipmentId) {
      return undefined;
    }

    sendMessage('join_shipment_room', { shipmentId: resolvedShipmentId });

    const handleShipmentEventUpdated = () => fetchAuditEvents({ silent: true });
    on('shipment_event_updated', handleShipmentEventUpdated);

    return () => {
      off('shipment_event_updated', handleShipmentEventUpdated);
      sendMessage('leave_shipment_room', { shipmentId: resolvedShipmentId });
    };
  }, [open, resolvedShipmentId, on, off, sendMessage, fetchAuditEvents]);

  const allEvents = auditData
    ? [...(auditData.events || []), ...(auditData.errors || [])].filter(
        (e) => !omittedEventTypes.includes(e.auditEventType)
      )
    : [];

  const sortedEvents =
    sortOrder === 'newest'
      ? [...allEvents].sort((a, b) => new Date(b.createdTimestamp) - new Date(a.createdTimestamp))
      : [...allEvents].sort((a, b) => new Date(a.createdTimestamp) - new Date(b.createdTimestamp));

  const getItemTimestamp = (item) => {
    if (item.type === 'event') return item.event.createdTimestamp;
    return item.events.reduce(
      (min, e) => (new Date(e.createdTimestamp) < new Date(min) ? e.createdTimestamp : min),
      item.events[0].createdTimestamp
    );
  };

  const renderItems = buildRenderItems(sortedEvents).sort((a, b) => {
    const ta = new Date(getItemTimestamp(a)).getTime();
    const tb = new Date(getItemTimestamp(b)).getTime();
    return sortOrder === 'newest' ? tb - ta : ta - tb;
  });

  const dateGroups = renderItems.reduce((acc, item) => {
    const key = format(new Date(getItemTimestamp(item)), 'MMM dd, yyyy');
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  let statusBarInfo = null;
  if (sortedEvents.length) {
    const newestFirst = [...allEvents].sort(
      (a, b) => new Date(b.createdTimestamp) - new Date(a.createdTimestamp)
    );
    const latest = newestFirst[0];
    const carrierEvent = newestFirst.find((e) => e.auditEventData?.carrier);
    const labelEvent =
      newestFirst.find((e) => e.auditEventType === 'SHIPMENT_LABEL_CREATED') ||
      newestFirst.find((e) => e.auditEventType === 'ORDER_SHIPPED');
    const carrierName = carrierEvent
      ? formatCarrierService(carrierEvent.auditEventData.carrier)
      : '';
    const serviceName = carrierEvent
      ? formatCarrierService(carrierEvent.auditEventData.carrierServiceCode)
      : '';
    const isVoidedAfterLabel =
      labelEvent &&
      sortedEvents.some(
        (e) =>
          e.auditEventType === 'SHIPMENT_VOIDED' &&
          new Date(e.createdTimestamp) > new Date(labelEvent.createdTimestamp)
      );
    const isLatestShipstationShipped =
      latest.auditEventType === 'ORDER_SHIPPED' && latest.shipmentSource === 'SHIPSTATION';
    const latestShippedDate = latest.auditEventData?.shippedDate;
    const [latestShipYear, latestShipMonth, latestShipDay] = (latestShippedDate || '').split('-');
    statusBarInfo = {
      statusLabel:
        statusLabelMap[latest.auditEventType] || latest.auditEventType.replace(/_/g, ' '),
      isFailed: failedEventTypes.includes(latest.auditEventType),
      carrier: serviceName || carrierName,
      cost:
        !isVoidedAfterLabel && labelEvent?.auditEventData?.cost != null
          ? `$${Number(labelEvent.auditEventData.cost).toFixed(2)}`
          : '',
      trackingNumber: !isVoidedAfterLabel ? labelEvent?.auditEventData?.trackingNumber || '' : '',
      lastUpdated:
        isLatestShipstationShipped && latestShippedDate
          ? `${latestShipMonth}/${latestShipDay}/${latestShipYear}`
          : format(new Date(latest.createdTimestamp), 'MMM dd, hh:mm a'),
      totalEvents: renderItems.length,
    };
  }

  const handleToggle = (id) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const handleSortChange = (_, val) => {
    if (val) setSortOrder(val);
  };
  const handleCopyJson = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick') onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{ sx: { minHeight: 500 } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          {/* Title row */}
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant='h6' fontWeight={700}>
                Shipping Timeline
              </Typography>
              {(referenceId || orderId) && (
                <Typography variant='body2' color='text.secondary'>
                  Order #<strong>{referenceId || orderId}</strong>
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Status bar */}
          {statusBarInfo && !loading && !error && (
            <Box
              sx={{
                px: 2.5,
                py: 1,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              {/* Info items — allowed to wrap */}
              <Box
                sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Status:{' '}
                  <Box
                    component='span'
                    sx={{
                      fontWeight: 700,
                      color: statusBarInfo.isFailed ? 'error.main' : 'primary.main',
                    }}
                  >
                    {statusBarInfo.statusLabel}
                  </Box>
                </Typography>
                {statusBarInfo.carrier && (
                  <>
                    <Divider orientation='vertical' flexItem />
                    <Typography variant='body2' color='text.secondary'>
                      Carrier: <strong>{statusBarInfo.carrier}</strong>
                    </Typography>
                  </>
                )}
                {statusBarInfo.cost && (
                  <>
                    <Divider orientation='vertical' flexItem />
                    <Typography variant='body2' color='text.secondary'>
                      Cost:{' '}
                      <Box component='span' sx={{ fontWeight: 700, color: 'success.main' }}>
                        {statusBarInfo.cost}
                      </Box>
                    </Typography>
                  </>
                )}
                <Divider orientation='vertical' flexItem />
                <Typography variant='body2' color='text.secondary'>
                  Latest: <strong>{statusBarInfo.lastUpdated}</strong>
                </Typography>
                <Divider orientation='vertical' flexItem />
                <Typography variant='body2' color='text.secondary'>
                  Events:{' '}
                  <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {statusBarInfo.totalEvents}
                  </Box>
                </Typography>
              </Box>

              {/* Sort arrows — always pinned right, never wrap */}
              <Box sx={{ flexShrink: 0 }}>
                <ToggleButtonGroup
                  value={sortOrder}
                  exclusive
                  onChange={handleSortChange}
                  size='small'
                >
                  <ToggleButton value='newest'>
                    <Tooltip title='Newest first'>
                      <ArrowDownwardIcon fontSize='small' />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value='oldest'>
                    <Tooltip title='Oldest first'>
                      <ArrowUpwardIcon fontSize='small' />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowX: 'hidden' }}>
          {loading && (
            <Box sx={{ px: 2, pt: 2 }}>
              {['s1', 's2', 's3', 's4', 's5', 's6'].map((id, i) => (
                <Box
                  key={id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ width: 150, flexShrink: 0, pr: 1.5 }}>
                    <Skeleton variant='text' width={80} sx={{ ml: 'auto' }} />
                    <Skeleton variant='text' width={70} sx={{ ml: 'auto' }} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: 22,
                      flexShrink: 0,
                      pt: 0.5,
                    }}
                  >
                    <Skeleton variant='circular' width={13} height={13} />
                  </Box>
                  <Box sx={{ flex: 1, pl: 1.5 }}>
                    <Skeleton variant='text' width={`${40 + (i % 3) * 20}%`} />
                    {i % 2 === 0 && <Skeleton variant='text' width='30%' />}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {error && (
            <Grid
              container
              direction='column'
              justifyContent='center'
              alignItems='center'
              sx={{ minHeight: 300, gap: 1 }}
            >
              <Typography variant='h6' color='error'>
                Unable to load timeline
              </Typography>
              <Typography variant='body2' color='text.secondary' align='center'>
                {error}
              </Typography>
            </Grid>
          )}
          {!loading && !error && isEmpty(renderItems) && (
            <Grid
              container
              direction='column'
              justifyContent='center'
              alignItems='center'
              sx={{
                minHeight: 300,
                gap: 2,
                '@keyframes fadeSlideUp': {
                  from: { opacity: 0, transform: 'translateY(16px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: 'fadeSlideUp 0.4s ease-out',
              }}
            >
              <Box
                sx={{
                  width: 130,
                  height: 130,
                  borderRadius: '50%',
                  backgroundColor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '@keyframes driveIn': {
                    '0%': { transform: 'translateX(-80px)', opacity: 0 },
                    '60%': { transform: 'translateX(8px)', opacity: 1 },
                    '100%': { transform: 'translateX(0px)', opacity: 1 },
                  },
                  '& .MuiSvgIcon-root': {
                    animation: 'driveIn 0.6s ease-out forwards',
                  },
                }}
              >
                <LocalShippingOutlinedIcon sx={{ fontSize: 68, color: 'grey.400' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant='h6' fontWeight={600} color='text.secondary' gutterBottom>
                  {auditData && auditData.count === 0
                    ? 'No shipment record found'
                    : 'No shipping events recorded'}
                </Typography>
                <Typography
                  variant='body1'
                  color='text.disabled'
                  sx={{ maxWidth: 320, mx: 'auto' }}
                >
                  Shipment history will appear here once this order is processed.
                </Typography>
              </Box>
            </Grid>
          )}
          {!loading && !error && !isEmpty(renderItems) && (
            <Box>
              {Object.entries(dateGroups).map(([dateLabel, items]) => (
                <Box key={dateLabel}>
                  {/* Date group separator */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1.5, pb: 0.75 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography
                      variant='caption'
                      fontWeight={700}
                      color='text.secondary'
                      sx={{ px: 1.5, flexShrink: 0 }}
                    >
                      {dateLabel}
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  {items.map((item, idx) => {
                    const isLast = idx === items.length - 1;

                    /* ── group row (Print Job / Label Retrieval) ── */
                    if (item.type === 'group') {
                      const {
                        correlationId,
                        label: groupLabel,
                        events: groupEvents,
                        hasFailed,
                      } = item;
                      const groupTs = getItemTimestamp(item);
                      const isOpen = expandedIds.includes(correlationId);
                      const dotColor = hasFailed ? 'error' : 'info';

                      return (
                        <Box
                          key={correlationId}
                          sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            px: 2,
                            py: 1.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          {/* timestamp */}
                          <Box
                            sx={{
                              width: 150,
                              flexShrink: 0,
                              textAlign: 'right',
                              pr: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              pt: 0.25,
                            }}
                          >
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ display: 'block', lineHeight: 1.5 }}
                            >
                              {format(new Date(groupTs), 'MM/dd/yyyy')}
                            </Typography>
                            <Typography
                              variant='body2'
                              color='primary.main'
                              fontWeight={700}
                              sx={{ display: 'block', lineHeight: 1.5 }}
                            >
                              {format(new Date(groupTs), 'hh:mm:ss a')}
                            </Typography>
                          </Box>

                          {/* dot + line */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              flexShrink: 0,
                              width: 22,
                            }}
                          >
                            <Box
                              sx={{
                                width: 13,
                                height: 13,
                                borderRadius: '50%',
                                bgcolor: `${dotColor}.main`,
                                flexShrink: 0,
                                mt: 0.4,
                              }}
                            />
                            {!isLast && (
                              <Box
                                sx={{
                                  width: 2,
                                  flex: 1,
                                  bgcolor: 'success.light',
                                  mt: 0.5,
                                  minHeight: 12,
                                }}
                              />
                            )}
                          </Box>

                          {/* details */}
                          <Box sx={{ flex: 1, minWidth: 0, pl: 1.5 }}>
                            <Typography
                              variant='body1'
                              fontWeight={600}
                              color={hasFailed ? 'error.main' : 'text.primary'}
                            >
                              {groupLabel}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {groupEvents.length} steps
                            </Typography>
                            <Typography
                              variant='body2'
                              color='primary.main'
                              sx={{ cursor: 'pointer', display: 'block', mt: 0.25 }}
                              onClick={() => handleToggle(correlationId)}
                            >
                              {isOpen ? 'Hide steps' : 'View steps >'}
                            </Typography>
                            <Collapse in={isOpen}>
                              <Box
                                sx={{
                                  mt: 0.75,
                                  pl: 1,
                                  borderLeft: '2px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {[...groupEvents]
                                  .sort((a, b) => {
                                    const ta = a.auditEventData?.timestamp || a.createdTimestamp;
                                    const tb = b.auditEventData?.timestamp || b.createdTimestamp;
                                    return new Date(tb) - new Date(ta);
                                  })
                                  .map((e) => {
                                    const subKeyInfo = getEventKeyInfo(e);
                                    const subKeyLines = Array.isArray(subKeyInfo)
                                      ? subKeyInfo
                                      : [subKeyInfo];
                                    const isFailed = failedEventTypes.includes(e.auditEventType);
                                    return (
                                      <Box key={e.auditEventId} sx={{ mb: 0.75 }}>
                                        <Box
                                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                          <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{ flexShrink: 0 }}
                                          >
                                            {format(
                                              new Date(
                                                e.auditEventData?.timestamp || e.createdTimestamp
                                              ),
                                              'hh:mm:ss a'
                                            )}
                                          </Typography>
                                          <Typography
                                            variant='caption'
                                            color={isFailed ? 'error.main' : 'text.primary'}
                                            sx={{ fontWeight: 600 }}
                                          >
                                            {eventTitleMap[e.auditEventType] ||
                                              e.auditEventType.replace(/_/g, ' ')}
                                          </Typography>
                                        </Box>
                                        {subKeyLines.filter(Boolean).map((line) => (
                                          <Typography
                                            key={line}
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{ display: 'block', pl: 0.5 }}
                                          >
                                            {line}
                                          </Typography>
                                        ))}
                                      </Box>
                                    );
                                  })}
                              </Box>
                            </Collapse>
                          </Box>
                        </Box>
                      );
                    }

                    /* ── regular event row ── */
                    const { event } = item;
                    const dotColor = getEventDotColor(event);
                    const isOpen = expandedIds.includes(event.auditEventId);
                    const diff = getEventDiff(event);
                    const rateDetail = getRateRequestDetail(event);
                    const itemDetail =
                      event.auditEventType === 'SHIPMENT_ITEM_ADDED' ||
                      event.auditEventType === 'SHIPMENT_ITEM_REMOVED'
                        ? (() => {
                            const data = event.auditEventData || {};
                            const movedItems =
                              event.auditEventType === 'SHIPMENT_ITEM_ADDED'
                                ? data.addedItems
                                : data.removedItems;
                            return Array.isArray(movedItems) ? movedItems : null;
                          })()
                        : null;
                    const expandLabel =
                      diff || rateDetail || (itemDetail && itemDetail.length > 0)
                        ? getExpandLabel(event) || 'View items >'
                        : null;
                    const keyInfo = getEventKeyInfo(event);
                    const keyLines = Array.isArray(keyInfo) ? keyInfo : [keyInfo];
                    const humanTitle =
                      eventTitleMap[event.auditEventType] ||
                      event.auditEventType.replace(/_/g, ' ');
                    let performedBy = '';
                    if (event.performedBy && event.performedBy !== 'SYSTEM') {
                      performedBy = event.performedBy;
                    } else if (event.auditEventData?.performedByName) {
                      performedBy = casingFormat(event.auditEventData.performedByName);
                    }

                    return (
                      <Box
                        key={event.auditEventId}
                        sx={{
                          display: 'flex',
                          alignItems: 'stretch',
                          px: 2,
                          py: 1.25,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          ...(newEventIds.has(event.auditEventId) && {
                            animation: 'newEventSlideIn 0.4s ease-out',
                            '@keyframes newEventSlideIn': {
                              from: { opacity: 0, transform: 'translateY(-6px)' },
                              to: { opacity: 1, transform: 'translateY(0)' },
                            },
                          }),
                        }}
                      >
                        {/* Left — timestamp */}
                        <Box
                          sx={{
                            width: 150,
                            flexShrink: 0,
                            textAlign: 'right',
                            pr: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            pt: 0.25,
                          }}
                        >
                          {(() => {
                            const isShipstationShipped =
                              event.auditEventType === 'ORDER_SHIPPED' &&
                              event.shipmentSource === 'SHIPSTATION';
                            const shippedDate = event.auditEventData?.shippedDate;
                            if (isShipstationShipped && shippedDate) {
                              const [year, month, date] = shippedDate.split('-');
                              return (
                                <Typography
                                  variant='body2'
                                  color='primary.main'
                                  fontWeight={700}
                                  sx={{ display: 'block', lineHeight: 1.5 }}
                                >
                                  {`${month}/${date}/${year}`}
                                </Typography>
                              );
                            }
                            return (
                              <>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  sx={{ display: 'block', lineHeight: 1.5 }}
                                >
                                  {format(new Date(event.createdTimestamp), 'MM/dd/yyyy')}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='primary.main'
                                  fontWeight={700}
                                  sx={{ display: 'block', lineHeight: 1.5 }}
                                >
                                  {format(new Date(event.createdTimestamp), 'hh:mm:ss a')}
                                </Typography>
                              </>
                            );
                          })()}
                          {performedBy && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ display: 'block', lineHeight: 1.4, fontStyle: 'italic' }}
                            >
                              {performedBy}
                            </Typography>
                          )}
                        </Box>

                        {/* Center — dot + vertical line */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexShrink: 0,
                            width: 22,
                          }}
                        >
                          <Box
                            sx={{
                              width: 13,
                              height: 13,
                              borderRadius: '50%',
                              bgcolor: `${dotColor}.main`,
                              flexShrink: 0,
                              mt: 0.4,
                            }}
                          />
                          {!isLast && (
                            <Box
                              sx={{
                                width: 2,
                                flex: 1,
                                bgcolor: 'success.light',
                                mt: 0.5,
                                minHeight: 12,
                              }}
                            />
                          )}
                        </Box>

                        {/* Right — event details */}
                        <Box sx={{ flex: 1, minWidth: 0, pl: 1.5 }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                          >
                            <Typography
                              variant='body1'
                              fontWeight={600}
                              color={getTitleColor(event.status)}
                            >
                              {humanTitle}
                            </Typography>
                            {newEventIds.has(event.auditEventId) && (
                              <Chip
                                label='New'
                                size='small'
                                color='success'
                                sx={{
                                  height: 16,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  transition: 'opacity 0.8s ease-out',
                                  opacity: fadingEventIds.has(event.auditEventId) ? 0 : 1,
                                }}
                              />
                            )}
                          </Box>
                          {event.shipmentSource &&
                            sourceTagEventTypes.has(event.auditEventType) && (
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ display: 'block' }}
                              >
                                {shipmentSourceLabel[event.shipmentSource] || event.shipmentSource}
                                {keyLines.filter(Boolean).length > 0 &&
                                  ` · ${keyLines.filter(Boolean)[0]}`}
                              </Typography>
                            )}
                          {keyLines.filter(Boolean).map((line, i) => {
                            if (
                              i === 0 &&
                              event.shipmentSource &&
                              sourceTagEventTypes.has(event.auditEventType)
                            )
                              return null;
                            return (
                              <Typography
                                key={line}
                                variant='body2'
                                color='text.secondary'
                                sx={{ display: 'block' }}
                              >
                                {line}
                              </Typography>
                            );
                          })}
                          {expandLabel && (
                            <Typography
                              variant='body2'
                              color='primary.main'
                              sx={{ cursor: 'pointer', display: 'block', mt: 0.25 }}
                              onClick={() => handleToggle(event.auditEventId)}
                            >
                              {isOpen ? 'Hide' : expandLabel}
                            </Typography>
                          )}
                          {itemDetail && itemDetail.length > 0 && (
                            <Collapse in={isOpen}>
                              <Box
                                sx={{
                                  mt: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  maxWidth: 620,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: 'grey.50',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 140,
                                      flexShrink: 0,
                                      px: 1.25,
                                      py: 0.75,
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}
                                    >
                                      ORDER ID
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      px: 1.25,
                                      py: 0.75,
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}
                                    >
                                      DRUG NAME
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: 100,
                                      flexShrink: 0,
                                      px: 1.25,
                                      py: 0.75,
                                      borderRight: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}
                                    >
                                      NDC
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: 80,
                                      flexShrink: 0,
                                      px: 1.25,
                                      py: 0.75,
                                      textAlign: 'right',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ fontWeight: 700, letterSpacing: 0.4, display: 'block' }}
                                    >
                                      QUANTITY
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box
                                  sx={{
                                    maxHeight: 180,
                                    overflowY: 'auto',
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': { display: 'none' },
                                  }}
                                >
                                  {itemDetail.map((it) => {
                                    const key =
                                      it.referenceId || `${it.sku || ''}-${it.name || ''}`;
                                    const name = it.name || it.referenceId || it.sku || 'Item';
                                    const qty = it.quantity != null ? String(it.quantity) : '—';
                                    const sku = it.sku || '—';
                                    const itemOrderId =
                                      it.externalOriginReferenceId ||
                                      it.externalOriginId ||
                                      it.externalOriginReferenceID ||
                                      '—';
                                    return (
                                      <Box
                                        key={key}
                                        sx={{
                                          display: 'flex',
                                          borderBottom: '1px solid',
                                          borderColor: 'divider',
                                          '&:last-child': { borderBottom: 'none' },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 140,
                                            flexShrink: 0,
                                            px: 1.25,
                                            py: 0.75,
                                            bgcolor: 'grey.50',
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Typography
                                            variant='body2'
                                            sx={{ fontWeight: 600 }}
                                            noWrap
                                          >
                                            {itemOrderId}
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            px: 1.25,
                                            py: 0.75,
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Typography
                                            variant='body2'
                                            sx={{ fontWeight: 600 }}
                                            noWrap
                                          >
                                            {name}
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            width: 100,
                                            flexShrink: 0,
                                            px: 1.25,
                                            py: 0.75,
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Typography variant='body2' color='text.secondary'>
                                            {sku}
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            width: 80,
                                            flexShrink: 0,
                                            px: 1.25,
                                            py: 0.75,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                          }}
                                        >
                                          <Typography variant='body2' color='text.secondary'>
                                            {qty}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                          {diff && (
                            <Collapse in={isOpen}>
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  maxWidth: 380,
                                }}
                              >
                                <Box sx={{ mb: 1 }}>
                                  <Typography
                                    variant='caption'
                                    color='text.primary'
                                    sx={{
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      display: 'block',
                                      mb: 0.25,
                                    }}
                                  >
                                    PREVIOUS ADDRESS
                                  </Typography>
                                  <Typography variant='body2' color='text.secondary'>
                                    {diff.before}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant='caption'
                                    color='success.main'
                                    sx={{
                                      fontWeight: 700,
                                      letterSpacing: 0.5,
                                      display: 'block',
                                      mb: 0.25,
                                    }}
                                  >
                                    UPDATED ADDRESS
                                  </Typography>
                                  <Typography variant='body2' color='text.primary'>
                                    {diff.after}
                                  </Typography>
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                          {rateDetail && (
                            <Collapse in={isOpen}>
                              <Box
                                sx={{
                                  mt: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  maxWidth: 420,
                                }}
                              >
                                {rateDetail.rows.map(({ label, value }, i) => (
                                  <Box
                                    key={label}
                                    sx={{
                                      display: 'flex',
                                      borderBottom:
                                        i < rateDetail.rows.length - 1 || rateDetail.address
                                          ? '1px solid'
                                          : 'none',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 90,
                                        flexShrink: 0,
                                        px: 1.25,
                                        py: 0.6,
                                        bgcolor: 'grey.50',
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          fontSize: 10,
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        {label}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ px: 1.25, py: 0.6, flex: 1 }}>
                                      <Typography variant='body2'>{value}</Typography>
                                    </Box>
                                  </Box>
                                ))}
                                {rateDetail.address && (
                                  <Box sx={{ display: 'flex' }}>
                                    <Box
                                      sx={{
                                        width: 90,
                                        flexShrink: 0,
                                        px: 1.25,
                                        py: 0.6,
                                        bgcolor: 'grey.50',
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          fontSize: 10,
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        Address
                                      </Typography>
                                    </Box>
                                    <Box sx={{ px: 1.25, py: 0.6, flex: 1 }}>
                                      <Typography variant='body2'>{rateDetail.address}</Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          )}
                        </Box>

                        {/* View Json button */}
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => setJsonDialogData(event.auditEventData)}
                          sx={{
                            ml: 1,
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            fontSize: 11,
                            px: 1,
                            py: 0.25,
                            minWidth: 0,
                            color: 'text.secondary',
                            borderColor: 'divider',
                            '&:hover': { borderColor: 'text.secondary' },
                          }}
                        >
                          View Json
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant='contained' size='small'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* JSON dialog */}
      <Dialog
        open={!!jsonDialogData}
        onClose={() => setJsonDialogData(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>Event Data</Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
              <IconButton size='small' onClick={() => handleCopyJson(jsonDialogData)}>
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 300 }}>
          {jsonDialogData && (
            <JsonView collapsed={3} value={jsonDialogData} enableClipboard={false} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogData(null)} variant='contained' size='small'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ShippingTimelineModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shipmentId: PropTypes.string,
  referenceId: PropTypes.string,
  orderId: PropTypes.string,
};

ShippingTimelineModal.defaultProps = {
  shipmentId: null,
  referenceId: null,
  orderId: null,
};

export default ShippingTimelineModal;
